// Coaching program purchase logic: PDF-only vs PDF+VIP selection, combined
// Ajax Cart API submission, and the mobile sticky CTA. A single shared state
// model drives every coaching-program-purchase instance on the page (main
// section + final CTA + sticky bar) so they can never drift out of sync.

if (!customElements.get('coaching-program-purchase')) {
  customElements.define(
    'coaching-program-purchase',
    class CoachingProgramPurchase extends HTMLElement {
      static instances = [];
      static selection = 'base';
      static isSubmitting = false;

      connectedCallback() {
        this.form = this.querySelector('form');
        this.radios = Array.from(this.querySelectorAll('input[type="radio"]'));
        this.cta = this.querySelector('[data-purchase-cta]');
        this.statusEl = this.querySelector('[data-purchase-status]');
        this.errorEl = this.querySelector('[data-purchase-error]');

        this.baseVariantId = this.dataset.baseVariantId;
        this.baseVariantAvailable = this.dataset.baseVariantAvailable === 'true';
        this.vipVariantId = this.dataset.vipVariantId || '';
        this.vipVariantAvailable = this.dataset.vipVariantAvailable === 'true';
        this.baseLabel = this.dataset.baseLabel || '';
        this.comboLabel = this.dataset.comboLabel || '';

        CoachingProgramPurchase.instances.push(this);

        this.radios.forEach((radio) => {
          radio.addEventListener('change', () => {
            CoachingProgramPurchase.setSelection(radio.value);
          });
        });

        this.form?.addEventListener('submit', (event) => {
          event.preventDefault();
          CoachingProgramPurchase.submit();
        });

        this.render();
      }

      disconnectedCallback() {
        CoachingProgramPurchase.instances = CoachingProgramPurchase.instances.filter((i) => i !== this);
      }

      render() {
        const selection = CoachingProgramPurchase.selection;
        this.radios.forEach((radio) => {
          radio.checked = radio.value === selection;
          radio.closest('.coaching-program-purchase__option')?.classList.toggle('is-selected', radio.checked);
        });
        if (this.cta) {
          this.cta.textContent = selection === 'combo' ? this.comboLabel : this.baseLabel;
        }
      }

      static setSelection(value) {
        CoachingProgramPurchase.selection = value;
        CoachingProgramPurchase.instances.forEach((instance) => instance.render());
      }

      static setBusy(isBusy) {
        CoachingProgramPurchase.instances.forEach((instance) => {
          if (!instance.cta) return;
          if (isBusy) {
            instance.cta.dataset.restoreLabel = instance.cta.textContent;
            instance.cta.textContent = 'Getting your program ready…';
          } else if (instance.cta.dataset.restoreLabel) {
            instance.cta.textContent = instance.cta.dataset.restoreLabel;
            delete instance.cta.dataset.restoreLabel;
          }
          instance.cta.setAttribute('aria-busy', String(isBusy));
          instance.cta.disabled = isBusy;
          if (instance.statusEl) instance.statusEl.hidden = !isBusy;
        });
      }

      static showError(message) {
        CoachingProgramPurchase.instances.forEach((instance) => {
          if (!instance.errorEl) return;
          instance.errorEl.textContent = message;
          instance.errorEl.hidden = false;
        });
      }

      static clearError() {
        CoachingProgramPurchase.instances.forEach((instance) => {
          if (instance.errorEl) instance.errorEl.hidden = true;
        });
      }

      static async submit() {
        if (CoachingProgramPurchase.isSubmitting) return;
        const first = CoachingProgramPurchase.instances[0];
        if (!first) return;

        CoachingProgramPurchase.clearError();

        if (!first.baseVariantAvailable) {
          CoachingProgramPurchase.showError("This program isn't available right now. Please check back soon.");
          return;
        }

        const items = [{ id: first.baseVariantId, quantity: 1 }];
        if (CoachingProgramPurchase.selection === 'combo') {
          if (!first.vipVariantId || !first.vipVariantAvailable) {
            CoachingProgramPurchase.showError("VIP coaching isn't available right now — you can still start with your training program above.");
            return;
          }
          items.push({ id: first.vipVariantId, quantity: 1 });
        }

        CoachingProgramPurchase.isSubmitting = true;
        CoachingProgramPurchase.setBusy(true);

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });

          if (!response.ok) {
            const payload = await response.json().catch(() => null);
            throw new Error(payload?.description || 'Something went wrong adding this to your cart.');
          }

          window.location.href = '/cart';
        } catch (error) {
          CoachingProgramPurchase.showError(error.message || 'Something went wrong. Please try again.');
          CoachingProgramPurchase.isSubmitting = false;
          CoachingProgramPurchase.setBusy(false);
        }
      }
    }
  );
}

// Mobile sticky CTA: mirrors the main purchase section's state, appears only
// once the visitor scrolls past the main purchase component, and disables
// itself once a submission is in progress.
(function () {
  function init() {
    const sticky = document.querySelector('[data-coaching-sticky]');
    const mainPurchase = document.querySelector('[data-purchase-placement="main"]');
    if (!sticky || !mainPurchase || sticky.dataset.enabled === undefined) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const mainVisible = entries[0].isIntersecting;
        sticky.classList.toggle('is-visible', !mainVisible);
      },
      { threshold: 0 }
    );
    observer.observe(mainPurchase);

    const stickyForm = sticky.querySelector('coaching-program-purchase');
    stickyForm?.addEventListener('submit', () => {
      sticky.classList.remove('is-visible');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

// FAQ accordion: accessible, keyboard-operable, no framework. Collapsed
// panels are marked `inert` (in addition to the visual max-height clip) so
// their content is removed from the accessibility tree and tab order while
// closed - a max-height:0 clip alone still leaves it screen-reader-reachable.
(function () {
  function init() {
    document.querySelectorAll('[data-faq-trigger]').forEach((trigger) => {
      const panelId = trigger.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      panel?.setAttribute('inert', '');

      trigger.addEventListener('click', () => {
        const expanded = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!expanded));
        if (!panel) return;
        if (expanded) {
          panel.removeAttribute('data-open');
          panel.setAttribute('inert', '');
        } else {
          panel.setAttribute('data-open', '');
          panel.removeAttribute('inert');
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
